// One shared answer for the status strip and the screen: the held reply summary.
// kind: ready (summary) | notConnected | unknown. A failed read is never zero.
import { createReplyEventsClient, isNotConnected } from '@/lib/reply-events-client.js'

export const HELD_STALE_MS = 60000
export const UNKNOWN_HELD = Object.freeze({ kind: 'unknown' })
export const NOT_CONNECTED_HELD = Object.freeze({ kind: 'notConnected' })

export const heldFromList = list => ({ kind: 'ready', held: list.summary.held, lastReceivedAt: list.summary.lastReceivedAt })

// Results are ordered by when their request started, never by when they arrive. begin() hands out a
// ticket; a result with an older ticket than the one already shown is dropped, so a slow old answer
// cannot overwrite a newer state (for example an empty list after a newer not connected).
export function createHeldStore({ load, staleMs = HELD_STALE_MS, now = Date.now } = {}) {
  let cached = null
  let inFlight = null
  let counter = 0
  let applied = 0
  const listeners = new Set()
  const emit = () => listeners.forEach(fn => fn())
  const apply = (value, ticket) => {
    if (ticket < applied) return false
    applied = ticket
    cached = { at: now(), value }
    return true
  }
  return {
    peek: () => cached?.value ?? null,
    begin: () => ++counter,
    read() {
      if (cached && now() - cached.at < staleMs) return Promise.resolve(cached.value)
      if (!inFlight) {
        const ticket = ++counter
        const request = Promise.resolve().then(load).then(
          list => heldFromList(list),
          e => (isNotConnected(e) ? NOT_CONNECTED_HELD : UNKNOWN_HELD),
        ).then(value => (apply(value, ticket) ? value : (cached ? cached.value : UNKNOWN_HELD))).finally(() => { if (inFlight === request) inFlight = null })
        inFlight = request
      }
      return inFlight
    },
    publish(value, ticket = ++counter) { if (apply(value, ticket)) emit() },
    reset() { applied = ++counter; cached = null; inFlight = null; emit() },
    subscribe(fn) { listeners.add(fn); return () => listeners.delete(fn) },
  }
}

const stores = new Map()
export function heldStoreFor({ baseUrl, getToken, userId, sessionId, allowLocal = false, workspaceId = null }) {
  const key = `${baseUrl}|${userId}|${sessionId}|${workspaceId ?? ''}`
  if (!stores.has(key)) {
    if (stores.size > 4) stores.clear()
    let client = null
    try { client = createReplyEventsClient({ baseUrl, getToken, allowLocal, expectedUserId: userId, expectedSessionId: sessionId, workspaceId }) } catch { /* not configured */ }
    stores.set(key, createHeldStore({ load: () => (client ? client.list() : Promise.reject(new Error('not configured'))) }))
  }
  return stores.get(key)
}
